import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import Layout        from './components/layout/Layout';
import { FilterProvider } from './components/layout/FilterContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/common/ProtectedRoute';
import AuthRoute      from './components/common/AuthRoute';

import RWEDashboard    from './components/RWEDashboard';
import Glossary        from './components/Glossary';
import Login           from './components/Login';
import ForgotPassword  from './components/ForgotPassword';
import ResetPassword   from './components/ResetPassword';
import UserManagement  from './components/UserManagement';
import AuditLogs       from './components/AuditLogs';
import TermsModal      from './components/TermsModal';

import { getAuthData, clearAuthData } from './utils/auth';
import { logout as apiLogout, refreshAccessToken, acceptTerms as apiAcceptTerms } from './services/authService';

const App = () => {
  const [user, setUser]                         = useState(null);
  const [isAuthenticated, setIsAuthenticated]   = useState(false);
  const [isLoading, setIsLoading]               = useState(true);
  const [showTermsModal, setShowTermsModal]     = useState(false);
  const [isAcceptingTerms, setIsAcceptingTerms] = useState(false);
  const [isDecliningTerms, setIsDecliningTerms] = useState(false);
  const sessionRestored                         = useRef(false);

  // Restore session on app load
  useEffect(() => {
    // Prevent StrictMode double-invocation from firing two refresh requests
    if (sessionRestored.current) return;
    sessionRestored.current = true;

    const restoreSession = async () => {
      try {
        const { isValid, user: storedUser, needsRefresh } = getAuthData();

        if (isValid && storedUser) {
          setUser(storedUser);
          setIsAuthenticated(true);
          // If session restored but T&C not yet accepted, show modal
          if (!storedUser.terms_accepted) {
            setShowTermsModal(true);
          }
          return;
        }

        if (needsRefresh && storedUser) {
          const refreshedUser = await refreshAccessToken();
          if (refreshedUser) {
            setUser(refreshedUser);
            setIsAuthenticated(true);
            if (!refreshedUser.terms_accepted) {
              setShowTermsModal(true);
            }
            return;
          }
        }

        clearAuthData();
      } catch {
        clearAuthData();
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
  }, []);

  const handleLogin = (userData) => {
    // Tokens already stored by authService.login()
    // Update React state, then check if T&C needs to be accepted
    setUser(userData);
    setIsAuthenticated(true);
    if (!userData.terms_accepted) {
      setShowTermsModal(true);
    }
  };

  const handleAcceptTerms = async () => {
    setIsAcceptingTerms(true);
    try {
      await apiAcceptTerms();
      // Update local user state so the modal doesn't reappear
      const updatedUser = { ...user, terms_accepted: true, terms_accepted_at: new Date().toISOString() };
      setUser(updatedUser);
      // Only update the stored user object — leave tokens untouched
      localStorage.setItem('act_user', JSON.stringify(updatedUser));
      setShowTermsModal(false);
    } catch (err) {
      console.error('Failed to accept terms:', err);
      // Don't block the user — close modal and let them proceed
      // The modal will reappear on next login until accepted successfully
      setShowTermsModal(false);
    } finally {
      setIsAcceptingTerms(false);
    }
  };

  const handleDeclineTerms = async () => {
    setIsDecliningTerms(true);
    try {
      await apiLogout(); // revoke tokens on server + clear localStorage
    } catch {
      // Even if server call fails, clear local state
    } finally {
      setIsDecliningTerms(false);
      setShowTermsModal(false);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const handleLogout = async () => {
    await apiLogout(); // revokes refresh token on server + clears localStorage
    setUser(null);
    setIsAuthenticated(false);
    setShowTermsModal(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Loading ACTelligence...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          {/* Public auth routes */}
          <Route path="/login" element={
            <AuthRoute isAuthenticated={isAuthenticated}>
              <Login onLogin={handleLogin} />
            </AuthRoute>
          } />
          <Route path="/forgot-password" element={
            <AuthRoute isAuthenticated={isAuthenticated}>
              <ForgotPassword />
            </AuthRoute>
          } />
          <Route path="/reset-password" element={
            <AuthRoute isAuthenticated={isAuthenticated}>
              <ResetPassword />
            </AuthRoute>
          } />

          {/* Protected application routes */}
          <Route path="/" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <FilterProvider>
                <Layout user={user} onLogout={handleLogout} />
              </FilterProvider>
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<RWEDashboard />} />
            <Route path="glossary" element={<Glossary />} />

            {/* Superadmin only — stakeholder cannot access */}
            <Route path="user-management" element={
              <ProtectedRoute isAuthenticated={isAuthenticated} requiredRole="superadmin" userRole={user?.role}>
                <UserManagement />
              </ProtectedRoute>
            } />
            <Route path="audit-logs" element={
              <ProtectedRoute isAuthenticated={isAuthenticated} requiredRole="superadmin" userRole={user?.role}>
                <AuditLogs />
              </ProtectedRoute>
            } />
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>

        {/* Blocking T&C acceptance modal — shown after first login */}
        <TermsModal
          isOpen={showTermsModal}
          onAccept={handleAcceptTerms}
          onDecline={handleDeclineTerms}
          mode="accept"
          isAccepting={isAcceptingTerms}
          isDeclining={isDecliningTerms}
        />
      </Router>
    </ErrorBoundary>
  );
};

export default App;
